import {
  AnalyticsEvent,
  EventType,
  LinkName,
  ObjectInteraction,
  type Payload,
  type RoleRequirementsEvent,
  TrackingType,
  UnifiedAnalyticsEvent,
  type UnifiedPayload,
} from '../types';

import { spreadableTestingId } from './experiments';
import { decodeQuestionId, insertEventIf } from './helper';

interface AnswerSelectedPayload
  extends Payload<TrackingType.Link, LinkName.ClickOnAnswer> {
  objectInteraction:
    | ObjectInteraction.AnswerSelected
    | ObjectInteraction.AnswerUnselected;
  rrAnswerIds: string[];
  rrQuestionIds: string[];
}

interface UnifiedAnswerSelectedPayload
  extends UnifiedPayload<
    | UnifiedAnalyticsEvent.AnswerSelected
    | UnifiedAnalyticsEvent.AnswerUnselected
  > {
  isFirstInteraction: boolean;
  roleReqQuestionId: string;
  roleReqAnswerId: string;
}

const sendAnswerSelectionEvent =
  (
    onEvent: (event: RoleRequirementsEvent) => void,
    unifiedAnalytics: boolean,
  ) =>
  (
    answerId: string,
    questionId: string,
    questionnaireId: string,
    isSelectingAnswer: boolean,
    firstInteraction: boolean,
  ): void => {
    const decodedQuestionId = decodeQuestionId(questionId);

    const analyticsPayload = unifiedAnalytics
      ? {
          eventName: isSelectingAnswer
            ? (UnifiedAnalyticsEvent.AnswerSelected as const)
            : (UnifiedAnalyticsEvent.AnswerUnselected as const),
          currentPageSection: 'role requirements' as const,
          isFirstInteraction: firstInteraction,
          roleReqQuestionId: decodedQuestionId,
          roleReqQuestionnaireId: questionnaireId,
          roleReqAnswerId: answerId,
          ...spreadableTestingId(),
        }
      : ({
          trackingType: TrackingType.Link,
          linkName: LinkName.ClickOnAnswer,
          objectInteraction: isSelectingAnswer
            ? ObjectInteraction.AnswerSelected
            : ObjectInteraction.AnswerUnselected,
          eventName: [
            isSelectingAnswer
              ? AnalyticsEvent.AnswerSelected
              : AnalyticsEvent.AnswerUnselected,
            ...insertEventIf(firstInteraction, AnalyticsEvent.FirstInteraction),
          ],
          rrAnswerIds: [answerId],
          rrQuestionIds: [decodedQuestionId],
          ...spreadableTestingId(),
        } as AnswerSelectedPayload);

    const spreadableName = unifiedAnalytics
      ? {}
      : {
          name: isSelectingAnswer
            ? EventType.OptionsSelected
            : EventType.OptionsUnselected,
        };

    onEvent({
      data: [
        {
          payload: {
            analytics: analyticsPayload,
          },
          ...spreadableName,
        },
      ],
    });
  };

export {
  type AnswerSelectedPayload,
  type UnifiedAnswerSelectedPayload,
  sendAnswerSelectionEvent,
};
